import { config } from '@iheartjane/config';
import { useInfiniteQuery } from '@tanstack/react-query';
import type { QueryFunctionContext } from '@tanstack/react-query';

import { api } from '@jane/shared/data-access';
import type { StoreSpecial } from '@jane/shared/types';

import { queryKeys } from './storeSpecials.util';

interface StoreSpecialsPageArgs {
  includeHidden?: boolean;
  limit: number;
  pageOffset: number;
  storeId: string | number;
}

interface StoreSpecialsResponse {
  offset: number;
  specials: StoreSpecial[];
  total: number;
}

const getStoreSpecialsPage = async ({
  limit,
  pageOffset,
  storeId,
  includeHidden,
}: StoreSpecialsPageArgs) => {
  const params = new URLSearchParams({
    limit: `${limit}`,
    offset: `${pageOffset}`,
  });

  if (includeHidden !== undefined) {
    params.set('include_hidden', `${includeHidden}`);
  }

  return await api.get<StoreSpecialsResponse>(
    `${config.apiPath}/stores/${storeId}/specials/page?${params.toString()}`
  );
};

type SpecialsPageQueryResponse = StoreSpecialsResponse & { pageParam: number };

export const useGetStoreSpecialsPage = (
  storeId: string | number,
  queryOptions: object = {},
  limit = 15,
  includeHidden?: boolean
) => {
  const { enabled, ...restOptions } = queryOptions as { enabled?: unknown };

  return useInfiniteQuery({
    queryFn: async ({ pageParam = 0 }: QueryFunctionContext) => {
      const result = await getStoreSpecialsPage({
        includeHidden,
        storeId,
        pageOffset: pageParam,
        limit,
      });
      // The backend returns the offset that is used for the next call
      // so we are storing that as the pageParam
      return { ...result, pageParam: result.offset };
    },
    queryKey: queryKeys.storeSpecialsPage(storeId, limit, includeHidden),
    getNextPageParam: (lastPage: SpecialsPageQueryResponse) => {
      const isLastPage = lastPage.specials.length < limit;

      return isLastPage ? undefined : lastPage.pageParam;
    },
    ...restOptions,
    ...('enabled' in queryOptions && { enabled: Boolean(enabled) }),
  });
};
